import React, { useState, useEffect } from 'react';
import { get, post, put, patch, del } from '../utils/api';

export default function AdminDashboard({ user }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Canvas token management state
  const [tokenData, setTokenData] = useState(null);
  const [newToken, setNewToken] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenMessage, setTokenMessage] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Rubric report state
  const [rubricReport, setRubricReport] = useState(null);
  const [rubricLoading, setRubricLoading] = useState(false);
  const [rubricFilter, setRubricFilter] = useState('all'); // 'all', 'missing', 'has_rubric'
  const [rubricCourseFilter, setRubricCourseFilter] = useState('all');

  // User management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('assessor');
  const [inviteRate, setInviteRate] = useState('');
  const [inviteCourseIds, setInviteCourseIds] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editCourseIds, setEditCourseIds] = useState([]);
  const [editCoursesLoading, setEditCoursesLoading] = useState(false);

  // Course assignments expansion state
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [courseAssignmentsLoading, setCourseAssignmentsLoading] = useState(false);

  // ── BATCH 11: Delete user state ──
  const [deletingUser, setDeletingUser] = useState(null); // { id, name, email, total_decisions }
  const [deleteError, setDeleteError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── BATCH 11: Set password state ──
  const [pwUser, setPwUser] = useState(null); // { id, email }
  const [pwInput, setPwInput] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState(null);
  const [pwSuccess, setPwSuccess] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await get('/admin/dashboard');
      setData(result);
    } catch (err) {
      console.error('Admin load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTokens = async () => {
    try {
      const result = await get('/admin/canvas-tokens');
      setTokenData(result);
    } catch (err) {
      console.error('Load tokens error:', err);
    }
  };

  const loadRubricReport = async () => {
    setRubricLoading(true);
    try {
      const result = await get('/admin/rubric-report');
      setRubricReport(result);
    } catch (err) {
      console.error('Load rubric report error:', err);
    } finally {
      setRubricLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const [usersResult, dashResult] = await Promise.all([
        get('/admin/users'),
        get('/admin/dashboard')
      ]);
      setUsers(usersResult);
      // Extract courses list for assignment dropdowns
      if (dashResult.courses) {
        setAllCourses(dashResult.courses.filter(c => c.is_active));
      }
    } catch (err) {
      console.error('Load users error:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteMessage(null);
    try {
      const body = {
        name: inviteName.trim(),
        email: inviteEmail.trim(),
        role: inviteRole,
        course_ids: inviteCourseIds,
      };
      if (inviteRate.trim()) body.hourly_rate = parseFloat(inviteRate);
      await post('/auth/invite', body);
      const courseCount = inviteCourseIds.length;
      setInviteMessage({
        type: 'success',
        text: `Invite sent to ${inviteEmail.trim()} with ${courseCount} course${courseCount !== 1 ? 's' : ''} assigned. They'll receive an email with a link to set their password.`
      });
      setInviteName('');
      setInviteEmail('');
      setInviteRole('assessor');
      setInviteRate('');
      setInviteCourseIds([]);
      loadUsers();
    } catch (err) {
      setInviteMessage({ type: 'error', text: err.message || 'Failed to send invite' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      const body = {};
      if (editRole) body.role = editRole;
      if (editRate !== '') body.hourly_rate = editRate === '' ? null : parseFloat(editRate);
      await patch(`/admin/users/${userId}`, body);
      // Also update course assignments
      await put(`/admin/users/${userId}/courses`, { course_ids: editCourseIds });
      setEditingUser(null);
      setEditRole('');
      setEditRate('');
      setEditCourseIds([]);
      loadUsers();
      load(); // refresh dashboard too
    } catch (err) {
      alert('Update failed: ' + err.message);
    }
  };

  const startEditing = async (u) => {
    setEditingUser(u.id);
    setEditRole(u.role);
    setEditRate(u.hourly_rate || '');
    setEditCoursesLoading(true);
    try {
      const userCourses = await get(`/admin/users/${u.id}/courses`);
      setEditCourseIds(userCourses.map(c => c.id));
    } catch (err) {
      console.error('Load user courses error:', err);
      setEditCourseIds([]);
    } finally {
      setEditCoursesLoading(false);
    }
  };

  const expandCourse = async (courseId) => {
    if (expandedCourse === courseId) {
      setExpandedCourse(null);
      setCourseAssignments([]);
      return;
    }
    setExpandedCourse(courseId);
    setCourseAssignmentsLoading(true);
    try {
      const result = await get(`/admin/courses/${courseId}/assignments`);
      setCourseAssignments(result);
    } catch (err) {
      console.error('Load course assignments error:', err);
      setCourseAssignments([]);
    } finally {
      setCourseAssignmentsLoading(false);
    }
  };

  const toggleAssignmentMonitoring = async (assignmentId, currentValue) => {
    try {
      await patch(`/admin/assignments/${assignmentId}`, { is_monitored: !currentValue });
      // Refresh the assignments list
      setCourseAssignments(prev => prev.map(a =>
        a.id === assignmentId ? { ...a, is_monitored: !currentValue } : a
      ));
      // Refresh dashboard stats since monitored counts changed
      load();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  const toggleCourseSelection = (courseId, selectedIds, setSelectedIds) => {
    if (selectedIds.includes(courseId)) {
      setSelectedIds(selectedIds.filter(id => id !== courseId));
    } else {
      setSelectedIds([...selectedIds, courseId]);
    }
  };

  // ── BATCH 11: Delete user handler ──
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await del(`/admin/users/${deletingUser.id}`);
      setDeletingUser(null);
      loadUsers();
      load();
    } catch (err) {
      setDeleteError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── BATCH 11: Set password handler ──
  const handleSetPassword = async () => {
    if (!pwUser) return;
    setPwError(null);
    setPwSuccess(null);
    if (pwInput.length < 8) {
      setPwError('Password must be at least 8 characters');
      return;
    }
    if (pwInput !== pwConfirm) {
      setPwError('Passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      const result = await post(`/admin/users/${pwUser.id}/set-password`, { password: pwInput });
      setPwSuccess(result.message);
      setPwInput('');
      setPwConfirm('');
      loadUsers();
    } catch (err) {
      setPwError(err.message || 'Failed to set password');
    } finally {
      setPwLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === 'tokens') loadTokens(); }, [tab]);
  useEffect(() => { if (tab === 'rubrics') loadRubricReport(); }, [tab]);
  useEffect(() => { if (tab === 'users') loadUsers(); }, [tab]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await post('/admin/sync');
      setTimeout(load, 5000); // reload after a few seconds for sync to progress
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setTimeout(() => setSyncing(false), 5000);
    }
  };

  const handleAddToken = async () => {
    if (!newToken.trim()) return;
    setTokenLoading(true);
    setTokenMessage(null);
    try {
      const result = await post('/admin/canvas-tokens', { token: newToken.trim(), label: newLabel.trim() });
      setTokenMessage({ type: 'success', text: `Token added — ${result.activeCount} token(s) now active` });
      setNewToken('');
      setNewLabel('');
      setTestResult(null);
      loadTokens();
    } catch (err) {
      setTokenMessage({ type: 'error', text: err.message });
    } finally {
      setTokenLoading(false);
    }
  };

  const handleRemoveToken = async (index, label) => {
    if (!window.confirm(`Remove "${label}"? This takes effect immediately.`)) return;
    setTokenLoading(true);
    setTokenMessage(null);
    try {
      const result = await del(`/admin/canvas-tokens/${index}`);
      setTokenMessage({ type: 'success', text: `Token removed — ${result.activeCount} token(s) remaining` });
      setTestResult(null);
      loadTokens();
    } catch (err) {
      setTokenMessage({ type: 'error', text: err.message });
    } finally {
      setTokenLoading(false);
    }
  };

  const handleTestTokens = async () => {
    setTokenLoading(true);
    setTestResult(null);
    try {
      const result = await post('/admin/canvas-tokens/test');
      setTestResult(result);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTokenLoading(false);
    }
  };

  const toggleAvailability = async (userId, current) => {
    try {
      await patch(`/admin/users/${userId}`, { is_available: !current });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading || !data) {
    return <div style={{textAlign:'center',padding:'60px',color:'#9CA3AF'}}>Loading admin dashboard...</div>;
  }

  const { stats, assessors, courses, lastSync } = data;

  // ── BATCH 11: Modal overlay style ──
  const modalOverlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
  };
  const modalStyle = {
    background: '#fff', borderRadius: '10px', padding: '28px',
    width: '100%', maxWidth: '440px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
  };

  return (
    <div>
      {/* Stats row */}
      <div className="stats-grid">
        <div className="card stat-card">
          <div className="stat-value">{stats.pending || 0}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="card stat-card">
          <div className="stat-value">{stats.checked_out || 0}</div>
          <div className="stat-label">Checked Out</div>
        </div>
        <div className="card stat-card">
          <div className={`stat-value ${parseInt(stats.overdue) > 0 ? 'alert' : ''}`}>{stats.overdue || 0}</div>
          <div className="stat-label">Overdue (&gt;4d)</div>
        </div>
        <div className="card stat-card">
          <div className="stat-value">{stats.graded_today || 0}</div>
          <div className="stat-label">Graded Today</div>
        </div>
        <div className="card stat-card">
          <div className="stat-value">{stats.new_today || 0}</div>
          <div className="stat-label">New Today</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[['overview','Overview'],['users','User Management'],['assessors','Assessors'],['courses','Courses'],['rubrics','Rubric Report'],['tokens','Canvas Tokens'],['sync','Sync Status']].map(([key, label]) => (
          <button key={key} className={`tab ${tab===key?'active':''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="card">
          <div className="section-header">
            <h2>System Overview</h2>
            <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
          <table className="data-table">
            <tbody>
              <tr><td><strong>Total submissions tracked</strong></td><td>{stats.total_all || 0}</td></tr>
              <tr><td><strong>Total graded</strong></td><td>{stats.total_graded || 0}</td></tr>
              <tr><td><strong>Avg days pending</strong></td><td>{stats.avg_hours_pending ? `${(stats.avg_hours_pending / 24).toFixed(1)}d` : 'N/A'}</td></tr>
              <tr><td><strong>Last Canvas sync</strong></td>
                <td>{lastSync ? new Date(lastSync.last_sync_at).toLocaleString() : 'Never'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* User Management tab */}
      {tab === 'users' && (
        <div>
          {/* Invite form */}
          <div className="card" style={{marginBottom:'20px'}}>
            <div className="section-header">
              <h2>Invite New User</h2>
            </div>
            <p style={{color:'#6B7280', fontSize:'14px', marginBottom:'16px'}}>
              Send an email invitation to a new trainer or admin. They'll receive a link to set their own password.
            </p>

            {inviteMessage && (
              <div style={{
                padding: '10px 14px',
                marginBottom: '16px',
                borderRadius: '6px',
                backgroundColor: inviteMessage.type === 'success' ? '#ECFDF5' : '#FEF2F2',
                color: inviteMessage.type === 'success' ? '#065F46' : '#991B1B',
                fontSize: '14px'
              }}>
                {inviteMessage.text}
              </div>
            )}

            <div style={{display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'flex-end'}}>
              <div style={{flex:'1 1 200px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Full Name *</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={e => setInviteName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  style={{
                    width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB',
                    borderRadius:'6px', fontSize:'14px'
                  }}
                />
              </div>
              <div style={{flex:'1 1 250px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="e.g. jane@example.com"
                  style={{
                    width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB',
                    borderRadius:'6px', fontSize:'14px'
                  }}
                />
              </div>
              <div style={{flex:'0 0 140px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Role</label>
                <select
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  style={{
                    width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB',
                    borderRadius:'6px', fontSize:'14px', backgroundColor:'#fff'
                  }}
                >
                  <option value="assessor">Assessor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div style={{flex:'0 0 120px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Hourly Rate ($)</label>
                <input
                  type="number"
                  value={inviteRate}
                  onChange={e => setInviteRate(e.target.value)}
                  placeholder="Optional"
                  step="0.01"
                  min="0"
                  style={{
                    width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB',
                    borderRadius:'6px', fontSize:'14px'
                  }}
                />
              </div>
            </div>

            {/* Course assignment for invite */}
            {inviteRole === 'assessor' && allCourses.length > 0 && (
              <div style={{marginTop:'16px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'8px'}}>
                  Assign Courses ({inviteCourseIds.length} selected)
                </label>
                <div style={{
                  border:'1px solid #D1D5DB', borderRadius:'6px', padding:'8px',
                  maxHeight:'180px', overflowY:'auto', backgroundColor:'#FAFAFA'
                }}>
                  {allCourses.map(c => (
                    <label key={c.id} style={{
                      display:'flex', alignItems:'center', gap:'8px', padding:'4px 6px',
                      cursor:'pointer', fontSize:'13px', borderRadius:'4px',
                      backgroundColor: inviteCourseIds.includes(c.id) ? '#EFF6FF' : 'transparent'
                    }}>
                      <input
                        type="checkbox"
                        checked={inviteCourseIds.includes(c.id)}
                        onChange={() => toggleCourseSelection(c.id, inviteCourseIds, setInviteCourseIds)}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
                <div style={{display:'flex', gap:'8px', marginTop:'6px'}}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{fontSize:'11px', padding:'2px 8px'}}
                    onClick={() => setInviteCourseIds(allCourses.map(c => c.id))}
                  >Select All</button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{fontSize:'11px', padding:'2px 8px'}}
                    onClick={() => setInviteCourseIds([])}
                  >Clear All</button>
                </div>
              </div>
            )}

            <div style={{marginTop:'16px'}}>
              <button
                className="btn btn-primary"
                onClick={handleInviteUser}
                disabled={inviteLoading || !inviteName.trim() || !inviteEmail.trim()}
              >
                {inviteLoading ? 'Sending Invite...' : 'Send Invite'}
              </button>
            </div>
          </div>

          {/* Users list */}
          <div className="card">
            <div className="section-header">
              <h2>All Users</h2>
              <span style={{color:'#6B7280', fontSize:'14px'}}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
            </div>
            {usersLoading ? (
              <div style={{textAlign:'center',padding:'40px',color:'#9CA3AF'}}>Loading users...</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Courses</th>
                    <th>Status</th>
                    <th>Hourly Rate</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <React.Fragment key={u.id}>
                      <tr>
                        <td><strong>{u.name}</strong></td>
                        <td style={{fontSize:'13px'}}>{u.email}</td>
                        <td>
                          {editingUser === u.id ? (
                            <select
                              value={editRole}
                              onChange={e => setEditRole(e.target.value)}
                              style={{
                                padding:'4px 8px', border:'1px solid #D1D5DB',
                                borderRadius:'4px', fontSize:'13px'
                              }}
                            >
                              <option value="assessor">Assessor</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`badge ${u.role === 'admin' ? 'badge-checked' : 'badge-pending'}`}>
                              {u.role}
                            </span>
                          )}
                        </td>
                        <td>
                          {u.role === 'admin' ? (
                            <span style={{color:'#6B7280', fontSize:'13px'}}>All (admin)</span>
                          ) : (
                            <span style={{fontSize:'13px'}}>{u.assigned_courses || 0} course{parseInt(u.assigned_courses) !== 1 ? 's' : ''}</span>
                          )}
                        </td>
                        <td>
                          {u.last_login ? (
                            <span className="badge badge-graded">Active</span>
                          ) : (
                            <span className="badge badge-overdue">Invite Pending</span>
                          )}
                        </td>
                        <td>
                          {editingUser === u.id ? (
                            <input
                              type="number"
                              value={editRate}
                              onChange={e => setEditRate(e.target.value)}
                              placeholder="—"
                              step="0.01"
                              min="0"
                              style={{
                                width:'80px', padding:'4px 8px', border:'1px solid #D1D5DB',
                                borderRadius:'4px', fontSize:'13px'
                              }}
                            />
                          ) : (
                            u.hourly_rate ? `$${parseFloat(u.hourly_rate).toFixed(2)}` : <span style={{color:'#9CA3AF'}}>—</span>
                          )}
                        </td>
                        <td style={{fontSize:'13px', color:'#6B7280'}}>
                          {u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          {editingUser === u.id ? (
                            <div style={{display:'flex', gap:'6px'}}>
                              <button className="btn btn-primary btn-sm" onClick={() => handleUpdateUser(u.id)}>
                                Save
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={() => setEditingUser(null)}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                              <button className="btn btn-secondary btn-sm" onClick={() => startEditing(u)}>
                                Edit
                              </button>
                              <button className="btn btn-secondary btn-sm"
                                      onClick={() => toggleAvailability(u.id, u.is_available)}>
                                {u.is_available ? 'Set Unavailable' : 'Set Available'}
                              </button>
                              {/* ── BATCH 11: Set Password button ── */}
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => {
                                  setPwUser({ id: u.id, email: u.email, name: u.name });
                                  setPwInput('');
                                  setPwConfirm('');
                                  setPwError(null);
                                  setPwSuccess(null);
                                }}
                              >
                                Set Password
                              </button>
                              {/* ── BATCH 11: Delete button ── */}
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => {
                                  setDeletingUser({
                                    id: u.id,
                                    name: u.name,
                                    email: u.email,
                                    total_decisions: u.total_decisions
                                  });
                                  setDeleteError(null);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {/* Expanded edit row for course assignments */}
                      {editingUser === u.id && editRole !== 'admin' && (
                        <tr>
                          <td colSpan="8" style={{backgroundColor:'#F9FAFB', padding:'12px 16px'}}>
                            <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'8px'}}>
                              Assigned Courses ({editCourseIds.length} selected)
                            </label>
                            {editCoursesLoading ? (
                              <div style={{color:'#9CA3AF', fontSize:'13px'}}>Loading courses...</div>
                            ) : (
                              <>
                                <div style={{
                                  border:'1px solid #D1D5DB', borderRadius:'6px', padding:'8px',
                                  maxHeight:'180px', overflowY:'auto', backgroundColor:'#fff'
                                }}>
                                  {allCourses.map(c => (
                                    <label key={c.id} style={{
                                      display:'flex', alignItems:'center', gap:'8px', padding:'4px 6px',
                                      cursor:'pointer', fontSize:'13px', borderRadius:'4px',
                                      backgroundColor: editCourseIds.includes(c.id) ? '#EFF6FF' : 'transparent'
                                    }}>
                                      <input
                                        type="checkbox"
                                        checked={editCourseIds.includes(c.id)}
                                        onChange={() => toggleCourseSelection(c.id, editCourseIds, setEditCourseIds)}
                                      />
                                      {c.name}
                                    </label>
                                  ))}
                                </div>
                                <div style={{display:'flex', gap:'8px', marginTop:'6px'}}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{fontSize:'11px', padding:'2px 8px'}}
                                    onClick={() => setEditCourseIds(allCourses.map(c => c.id))}
                                  >Select All</button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{fontSize:'11px', padding:'2px 8px'}}
                                    onClick={() => setEditCourseIds([])}
                                  >Clear All</button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Assessors tab */}
      {tab === 'assessors' && (
        <div className="card">
          <div className="section-header">
            <h2>Assessor Workload</h2>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Checked Out</th>
                <th>Graded (7d)</th>
                <th>Total Graded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessors.map(a => (
                <tr key={a.id}>
                  <td><strong>{a.name}</strong></td>
                  <td>{a.role}</td>
                  <td>
                    <span className={`badge ${a.is_available ? 'badge-available' : 'badge-unavailable'}`}>
                      {a.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </td>
                  <td>{a.checked_out || 0}</td>
                  <td>{a.graded_7d || 0}</td>
                  <td>{a.total_graded || 0}</td>
                  <td>
                    <button className="btn btn-secondary btn-sm"
                            onClick={() => toggleAvailability(a.id, a.is_available)}>
                      {a.is_available ? 'Set Unavailable' : 'Set Available'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Courses tab */}
      {tab === 'courses' && (
        <div className="card">
          <div className="section-header">
            <h2>Courses</h2>
            <span style={{color:'#6B7280', fontSize:'13px'}}>Click a course to manage its assignments</span>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th></th>
                <th>Course</th>
                <th>Assigned Assessor</th>
                <th>Pending</th>
                <th>Overdue</th>
                <th>Assignments</th>
                <th>No Rubric</th>
              </tr>
            </thead>
            <tbody>
              {courses.map(c => (
                <React.Fragment key={c.id}>
                  <tr
                    style={{cursor:'pointer'}}
                    onClick={() => expandCourse(c.id)}
                  >
                    <td style={{width:'30px', textAlign:'center', fontSize:'12px', color:'#6B7280'}}>
                      {expandedCourse === c.id ? '▼' : '▶'}
                    </td>
                    <td><strong>{c.name}</strong></td>
                    <td>{c.assigned_assessor || <span style={{color:'#9CA3AF'}}>Pool</span>}</td>
                    <td>{c.pending || 0}</td>
                    <td className={parseInt(c.overdue) > 0 ? 'age-critical' : ''}>{c.overdue || 0}</td>
                    <td>
                      {c.monitored_assignments || 0} of {c.total_assignments || 0}
                      {parseInt(c.total_assignments) > parseInt(c.monitored_assignments || 0) && (
                        <span style={{color:'#9CA3AF', fontSize:'12px', marginLeft:'4px'}}>
                          ({parseInt(c.total_assignments) - parseInt(c.monitored_assignments || 0)} hidden)
                        </span>
                      )}
                    </td>
                    <td className={parseInt(c.no_rubric) > 0 ? 'age-warning' : ''}>{c.no_rubric || 0}</td>
                  </tr>
                  {expandedCourse === c.id && (
                    <tr>
                      <td colSpan="7" style={{padding:'0', backgroundColor:'#F9FAFB'}}>
                        {courseAssignmentsLoading ? (
                          <div style={{padding:'20px', textAlign:'center', color:'#9CA3AF', fontSize:'13px'}}>
                            Loading assignments...
                          </div>
                        ) : courseAssignments.length === 0 ? (
                          <div style={{padding:'20px', textAlign:'center', color:'#9CA3AF', fontSize:'13px'}}>
                            No assignments found for this course. Run a Canvas sync first.
                          </div>
                        ) : (
                          <div style={{padding:'12px 16px'}}>
                            <table style={{width:'100%', fontSize:'13px', borderCollapse:'collapse'}}>
                              <thead>
                                <tr style={{borderBottom:'1px solid #E5E7EB'}}>
                                  <th style={{textAlign:'left', padding:'6px 8px', color:'#6B7280', fontWeight:'600'}}>Monitored</th>
                                  <th style={{textAlign:'left', padding:'6px 8px', color:'#6B7280', fontWeight:'600'}}>Assignment</th>
                                  <th style={{textAlign:'left', padding:'6px 8px', color:'#6B7280', fontWeight:'600'}}>Rubric</th>
                                  <th style={{textAlign:'right', padding:'6px 8px', color:'#6B7280', fontWeight:'600'}}>Pending</th>
                                  <th style={{textAlign:'right', padding:'6px 8px', color:'#6B7280', fontWeight:'600'}}>Graded</th>
                                  <th style={{textAlign:'right', padding:'6px 8px', color:'#6B7280', fontWeight:'600'}}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {courseAssignments.map(a => (
                                  <tr key={a.id} style={{
                                    borderBottom:'1px solid #F3F4F6',
                                    opacity: a.is_monitored ? 1 : 0.5
                                  }}>
                                    <td style={{padding:'6px 8px'}}>
                                      <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}>
                                        <input
                                          type="checkbox"
                                          checked={a.is_monitored}
                                          onChange={(e) => { e.stopPropagation(); toggleAssignmentMonitoring(a.id, a.is_monitored); }}
                                          style={{width:'16px', height:'16px', cursor:'pointer'}}
                                        />
                                        <span style={{fontSize:'12px', color: a.is_monitored ? '#065F46' : '#991B1B'}}>
                                          {a.is_monitored ? 'Active' : 'Hidden'}
                                        </span>
                                      </label>
                                    </td>
                                    <td style={{padding:'6px 8px'}}>
                                      <strong>{a.name}</strong>
                                      {!a.published && (
                                        <span className="badge badge-unavailable" style={{marginLeft:'6px', fontSize:'10px'}}>
                                          Unpublished
                                        </span>
                                      )}
                                    </td>
                                    <td style={{padding:'6px 8px'}}>
                                      {a.has_rubric
                                        ? <span className="badge badge-graded" style={{fontSize:'11px'}}>Yes</span>
                                        : <span className="badge badge-overdue" style={{fontSize:'11px'}}>Missing</span>
                                      }
                                    </td>
                                    <td style={{padding:'6px 8px', textAlign:'right'}}>{a.pending_submissions || 0}</td>
                                    <td style={{padding:'6px 8px', textAlign:'right'}}>{a.graded_submissions || 0}</td>
                                    <td style={{padding:'6px 8px', textAlign:'right'}}>{a.total_submissions || 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rubric Report tab */}
      {tab === 'rubrics' && (
        <div>
          {rubricLoading ? (
            <div style={{textAlign:'center',padding:'40px',color:'#9CA3AF'}}>Loading rubric report...</div>
          ) : rubricReport ? (
            <>
              {/* Course summary cards */}
              <div className="card" style={{marginBottom:'20px'}}>
                <div className="section-header">
                  <h2>Course Rubric Coverage</h2>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Assignments</th>
                      <th>With Rubric</th>
                      <th>Missing Rubric</th>
                      <th>Coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rubricReport.courses.map(c => {
                      const published = parseInt(c.total_assignments) - parseInt(c.unpublished || 0);
                      const pct = published > 0 ? Math.round((parseInt(c.with_rubric) / published) * 100) : 0;
                      return (
                        <tr key={c.id}>
                          <td><strong>{c.course_name}</strong></td>
                          <td>{c.total_assignments} ({c.unpublished > 0 ? `${c.unpublished} unpublished` : 'all published'})</td>
                          <td>{c.with_rubric}</td>
                          <td className={parseInt(c.missing_rubric) > 0 ? 'age-warning' : ''}>{c.missing_rubric}</td>
                          <td>
                            <span className={`badge ${pct === 100 ? 'badge-graded' : pct >= 75 ? 'badge-pending' : 'badge-overdue'}`}>
                              {pct}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Assignment detail */}
              <div className="card">
                <div className="section-header">
                  <h2>Assignment Detail</h2>
                </div>
                <div className="filters" style={{marginBottom:'16px'}}>
                  <select value={rubricCourseFilter} onChange={e => setRubricCourseFilter(e.target.value)}>
                    <option value="all">All Courses</option>
                    {rubricReport.courses.map(c => (
                      <option key={c.id} value={c.id}>{c.course_name}</option>
                    ))}
                  </select>
                  <select value={rubricFilter} onChange={e => setRubricFilter(e.target.value)}>
                    <option value="all">All Assignments</option>
                    <option value="missing">Missing Rubric</option>
                    <option value="has_rubric">Has Rubric</option>
                  </select>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Assignment</th>
                      <th>Submission Types</th>
                      <th>Rubric</th>
                      <th>Criteria</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rubricReport.assignments
                      .filter(a => {
                        if (rubricCourseFilter !== 'all' && String(a.courseId) !== rubricCourseFilter) return false;
                        if (rubricFilter === 'missing') return !a.hasRubric && a.published;
                        if (rubricFilter === 'has_rubric') return a.hasRubric;
                        return true;
                      })
                      .map(a => (
                        <tr key={a.id} style={!a.published ? {opacity:0.5} : {}}>
                          <td>{a.courseName}</td>
                          <td>
                            <strong>{a.assignmentName}</strong>
                            {!a.published && <span className="badge badge-unavailable" style={{marginLeft:'6px'}}>Unpublished</span>}
                          </td>
                          <td>
                            {(a.submissionTypes || []).map(st => (
                              <span key={st} className="badge badge-checked" style={{marginRight:'4px', marginBottom:'2px'}}>
                                {st.replace(/_/g, ' ')}
                              </span>
                            ))}
                            {(!a.submissionTypes || a.submissionTypes.length === 0) &&
                              <span style={{color:'#9CA3AF'}}>—</span>
                            }
                          </td>
                          <td>
                            {a.hasRubric
                              ? <span className="badge badge-graded">Yes</span>
                              : <span className="badge badge-overdue">Missing</span>
                            }
                          </td>
                          <td>
                            {a.rubricCriteria.length > 0
                              ? a.rubricCriteria.map((rc, i) => (
                                  <div key={i} style={{fontSize:'12px', color:'#374151', marginBottom:'2px'}}>
                                    {rc.description} <span style={{color:'#9CA3AF'}}>({rc.points}pt)</span>
                                  </div>
                                ))
                              : <span style={{color:'#9CA3AF'}}>—</span>
                            }
                          </td>
                          <td>{a.pointsPossible || '—'}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="card" style={{padding:'40px',textAlign:'center',color:'#9CA3AF'}}>
              No data available. Run a Canvas sync first.
            </div>
          )}
        </div>
      )}

      {/* Canvas Tokens tab */}
      {tab === 'tokens' && (
        <div className="card">
          <div className="section-header">
            <h2>Canvas API Tokens</h2>
            <button className="btn btn-secondary btn-sm" onClick={handleTestTokens} disabled={tokenLoading}>
              {tokenLoading ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          {tokenMessage && (
            <div style={{
              padding: '10px 14px',
              marginBottom: '16px',
              borderRadius: '6px',
              backgroundColor: tokenMessage.type === 'success' ? '#ECFDF5' : '#FEF2F2',
              color: tokenMessage.type === 'success' ? '#065F46' : '#991B1B',
              fontSize: '14px'
            }}>
              {tokenMessage.text}
            </div>
          )}

          {testResult && (
            <div style={{
              padding: '10px 14px',
              marginBottom: '16px',
              borderRadius: '6px',
              backgroundColor: testResult.ok ? '#ECFDF5' : '#FEF2F2',
              color: testResult.ok ? '#065F46' : '#991B1B',
              fontSize: '14px'
            }}>
              {testResult.ok
                ? `Connected as "${testResult.user}" — ${testResult.tokensConfigured} token(s), ${testResult.requestsMade} requests made`
                : `Connection failed: ${testResult.error}`
              }
            </div>
          )}

          <p style={{color:'#6B7280', fontSize:'14px', marginBottom:'16px'}}>
            Tokens are used in round-robin to spread load across Canvas rate limits.
            {tokenData?.updatedBy && (
              <span> Last updated by <strong>{tokenData.updatedBy}</strong> on {new Date(tokenData.updatedAt).toLocaleString()}.</span>
            )}
          </p>

          {/* Existing tokens */}
          {tokenData?.tokens?.length > 0 ? (
            <table className="data-table" style={{marginBottom:'24px'}}>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Token (masked)</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokenData.tokens.map((t) => (
                  <tr key={t.index}>
                    <td><strong>{t.label}</strong></td>
                    <td style={{fontFamily:'monospace', fontSize:'13px'}}>{t.maskedToken}</td>
                    <td>{t.addedAt ? new Date(t.addedAt).toLocaleDateString() : '—'}</td>
                    <td>
                      <button className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveToken(t.index, t.label)}
                              disabled={tokenLoading}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{padding:'12px 0', color:'#9CA3AF', fontStyle:'italic'}}>
              No tokens stored yet. Add one below to enable Canvas integration.
            </p>
          )}

          {/* Add new token form */}
          <div style={{
            borderTop: '1px solid #E5E7EB',
            paddingTop: '20px',
          }}>
            <h3 style={{fontSize:'16px', marginBottom:'12px', color:'#374151'}}>Add New Token</h3>
            <div style={{display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'flex-end'}}>
              <div style={{flex:'0 0 200px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Label (optional)</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  placeholder="e.g. Paul's token"
                  style={{
                    width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB',
                    borderRadius:'6px', fontSize:'14px'
                  }}
                />
              </div>
              <div style={{flex:'1 1 300px'}}>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Canvas API Token</label>
                <input
                  type="password"
                  value={newToken}
                  onChange={e => setNewToken(e.target.value)}
                  placeholder="Paste token from Canvas > Account > Settings > New Access Token"
                  style={{
                    width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB',
                    borderRadius:'6px', fontSize:'14px', fontFamily:'monospace'
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddToken(); }}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddToken}
                disabled={tokenLoading || !newToken.trim()}
                style={{height:'38px', whiteSpace:'nowrap'}}
              >
                Add Token
              </button>
            </div>
            <p style={{color:'#9CA3AF', fontSize:'12px', marginTop:'8px'}}>
              Generate tokens in Canvas: Account → Settings → New Access Token. Tokens take effect immediately.
            </p>
          </div>
        </div>
      )}

      {/* Sync Status tab */}
      {tab === 'sync' && (
        <div className="card">
          <div className="section-header">
            <h2>Canvas Sync History</h2>
            <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Trigger Sync'}
            </button>
          </div>
          {lastSync ? (
            <table className="data-table">
              <thead>
                <tr><th>Time</th><th>Type</th><th>Items</th><th>Duration</th><th>Errors</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>{new Date(lastSync.created_at).toLocaleString()}</td>
                  <td>{lastSync.sync_type}</td>
                  <td>{lastSync.items_synced}</td>
                  <td>{lastSync.duration_ms ? `${(lastSync.duration_ms/1000).toFixed(1)}s` : '-'}</td>
                  <td>{lastSync.errors && JSON.parse(lastSync.errors || '[]').length > 0
                    ? <span className="badge badge-overdue">{JSON.parse(lastSync.errors).length} errors</span>
                    : <span className="badge badge-graded">Clean</span>}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p style={{padding:'20px', color:'#9CA3AF'}}>No syncs recorded yet. Click "Trigger Sync" to start.</p>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════
          BATCH 11: Set Password Modal
          ════════════════════════════════════════════════ */}
      {pwUser && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h2 style={{fontSize:'18px', marginBottom:'6px', color:'#00707F'}}>Set Password</h2>
            <p style={{fontSize:'13px', color:'#6B7280', marginBottom:'18px'}}>
              Setting password for: <strong>{pwUser.name}</strong> ({pwUser.email})
            </p>

            <label style={{display:'block', fontSize:'13px', fontWeight:'600', color:'#555', marginBottom:'4px'}}>
              New Password (min 8 characters)
            </label>
            <input
              type="password"
              value={pwInput}
              onChange={e => setPwInput(e.target.value)}
              placeholder="Enter new password"
              autoFocus
              style={{
                width:'100%', padding:'9px 12px', border:'1px solid #D1D5DB',
                borderRadius:'6px', fontSize:'14px', marginBottom:'12px'
              }}
            />

            <label style={{display:'block', fontSize:'13px', fontWeight:'600', color:'#555', marginBottom:'4px'}}>
              Confirm Password
            </label>
            <input
              type="password"
              value={pwConfirm}
              onChange={e => setPwConfirm(e.target.value)}
              placeholder="Confirm password"
              onKeyDown={e => { if (e.key === 'Enter') handleSetPassword(); }}
              style={{
                width:'100%', padding:'9px 12px', border:'1px solid #D1D5DB',
                borderRadius:'6px', fontSize:'14px', marginBottom:'14px'
              }}
            />

            {pwError && (
              <div style={{
                padding:'9px 12px', borderRadius:'6px', marginBottom:'14px',
                backgroundColor:'#FEF2F2', color:'#991B1B', fontSize:'13px'
              }}>
                {pwError}
              </div>
            )}

            {pwSuccess && (
              <div style={{
                padding:'9px 12px', borderRadius:'6px', marginBottom:'14px',
                backgroundColor:'#ECFDF5', color:'#065F46', fontSize:'13px'
              }}>
                ✓ {pwSuccess}
              </div>
            )}

            <div style={{display:'flex', gap:'8px', justifyContent:'flex-end'}}>
              <button
                className="btn btn-secondary"
                onClick={() => { setPwUser(null); setPwInput(''); setPwConfirm(''); setPwError(null); setPwSuccess(null); }}
              >
                {pwSuccess ? 'Close' : 'Cancel'}
              </button>
              {!pwSuccess && (
                <button
                  className="btn btn-primary"
                  onClick={handleSetPassword}
                  disabled={pwLoading}
                >
                  {pwLoading ? 'Setting...' : 'Set Password'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════
          BATCH 11: Delete User Modal
          ════════════════════════════════════════════════ */}
      {deletingUser && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h2 style={{fontSize:'18px', marginBottom:'12px', color:'#DC2626'}}>Delete User</h2>
            <p style={{fontSize:'14px', marginBottom:'10px'}}>
              Are you sure you want to delete <strong>{deletingUser.name}</strong> ({deletingUser.email})?
            </p>
            {parseInt(deletingUser.total_decisions) > 0 && (
              <p style={{
                fontSize:'13px', color:'#92400E', padding:'10px 12px', borderRadius:'6px',
                backgroundColor:'#FFFBEB', marginBottom:'10px'
              }}>
                This user has <strong>{deletingUser.total_decisions}</strong> assessment decision(s) on record.
                Those decisions will be preserved — only the login account is removed.
              </p>
            )}
            <p style={{fontSize:'13px', color:'#6B7280', marginBottom:'18px'}}>
              This cannot be undone. Any submissions they have checked out will be released back to the queue.
            </p>

            {deleteError && (
              <div style={{
                padding:'9px 12px', borderRadius:'6px', marginBottom:'14px',
                backgroundColor:'#FEF2F2', color:'#991B1B', fontSize:'13px'
              }}>
                {deleteError}
              </div>
            )}

            <div style={{display:'flex', gap:'8px', justifyContent:'flex-end'}}>
              <button
                className="btn btn-secondary"
                onClick={() => { setDeletingUser(null); setDeleteError(null); }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteUser}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
